import React from 'react';
import PropTypes from 'prop-types';
import { Text, View, TextInput, Animated, Easing, Platform, StyleSheet } from 'react-native';

import renderNode from './renderNode';

import { Icon } from './Icon';
import { fontSizes } from '../../../styles';

const renderText = (content, defaultProps, style) =>
  renderNode(Text, content, {
    ...defaultProps,
    style: StyleSheet.flatten([style, defaultProps && defaultProps.style]),
  });

class Input extends React.Component {
  shakeAnimationValue = new Animated.Value(0);

  // eslint-disable-next-line react/sort-comp
  focus() {
    this.input.focus();
  }

  blur() {
    this.input.blur();
  }

  clear() {
    this.input.clear();
  }

  isFocused() {
    return this.input.isFocused();
  }

  setNativeProps(nativeProps) {
    this.input.setNativeProps(nativeProps);
  }

  shake = () => {
    const { shakeAnimationValue } = this;

    shakeAnimationValue.setValue(0);
    // Animation duration based on Material Design
    // https://material.io/guidelines/motion/duration-easing.html#duration-easing-common-durations
    Animated.timing(shakeAnimationValue, {
      duration: 375,
      toValue: 3,
      ease: Easing.bounce,
    }).start();
  };

  render() {
    const {
      containerStyle,
      disabled,
      disabledInputStyle,
      inputContainerStyle,
      leftIcon,
      leftIconContainerStyle,
      rightIcon,
      rightIconContainerStyle,
      inputComponent: InputComponent = TextInput,
      inputStyle,
      errorProps,
      errorStyle,
      errorMessage,
      label,
      labelStyle,
      labelProps,
      ...attributes
    } = this.props;

    const translateX = this.shakeAnimationValue.interpolate({
      inputRange: [0, 0.5, 1, 1.5, 2, 2.5, 3],
      outputRange: [0, -15, 0, 15, 0, -15, 0],
    });

    return (
      <View style={StyleSheet.flatten([styles.container, containerStyle])}>
        {renderText(label, { style: labelStyle, ...labelProps })}

        <Animated.View
          style={StyleSheet.flatten([inputContainerStyle, { transform: [{ translateX }] }])}
        >
          {leftIcon && (
            <View style={StyleSheet.flatten([styles.iconContainer, leftIconContainerStyle])}>
              {renderNode(Icon, leftIcon)}
            </View>
          )}

          <InputComponent
            testID="RNE__Input__text-input"
            underlineColorAndroid="transparent"
            editable={!disabled}
            {...attributes}
            ref={ref => {
              this.input = ref;
            }}
            style={StyleSheet.flatten([
              styles.input,
              inputStyle,
              disabled && styles.disabledInput,
              disabled && disabledInputStyle,
            ])}
          />

          {rightIcon && (
            <View style={StyleSheet.flatten([styles.iconContainer, rightIconContainerStyle])}>
              {renderNode(Icon, rightIcon)}
            </View>
          )}
        </Animated.View>

        {!!errorMessage && (
          <Text {...errorProps} style={StyleSheet.flatten([errorStyle && errorStyle])}>
            {errorMessage}
          </Text>
        )}
      </View>
    );
  }
}

Input.propTypes = {
  containerStyle: PropTypes.any,
  disabled: PropTypes.bool,
  disabledInputStyle: PropTypes.any,
  inputContainerStyle: PropTypes.any,
  leftIcon: PropTypes.any,
  leftIconContainerStyle: PropTypes.any,
  rightIcon: PropTypes.any,
  rightIconContainerStyle: PropTypes.any,
  inputStyle: PropTypes.any,
  inputComponent: PropTypes.elementType,
  errorProps: PropTypes.object,
  errorStyle: PropTypes.any,
  errorMessage: PropTypes.string,
  label: PropTypes.node,
  labelStyle: PropTypes.any,
  labelProps: PropTypes.object,
};

const styles = {
  container: {
    width: '100%',
    paddingHorizontal: 10,
  },
  disabledInput: {
    opacity: 0.5,
  },
  inputContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    alignItems: 'center',
    borderColor: 'grey',
  },
  iconContainer: {
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 15,
  },
  input: {
    alignSelf: 'center',
    color: 'black',
    fontSize: fontSizes.medium,
    fontFamily: 'Rubik-Regular',
    flex: 1,
    minHeight: 36,
  },
  error: {
    margin: 5,
    fontSize: 12,
    color: 'red',
  },
  label: {
    fontSize: 16,
    color: 'grey',
  },
};

export { Input };
export default Input;
